'use client';

import type { HandleChangeAnswerType } from '../model';
import type { AnswerChoicesRankingScaleType, AnswerUserType } from '~/model/AnswerUserType';
import type { ChoiceType } from '~/model/ChoiceType';
import Slider from 'rc-slider';
import { useLayoutEffect } from 'react';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { useFocus } from '~/hooks/useFocus';
import { itemsLetters } from '../utils';

export const RankingScaleTypeQuestion = ({
  answerUser,
  answer_choices,
  questionId,
  label_min,
  label_max,
  limit_min: limitMin,
  limit_max: limitMax,
  handleChangeAnswer,
  type,
  langValue,
  disabled = false,
}: RankingScaleTypeQuestionPropsType) => {
  const [handleRef, setHandleFocus] = useFocus();
  // Get 1/16% of ranking scale's total range
  const spaceBetweenHandle
    = limitMin !== null
      && limitMax !== null
      && Math.floor((limitMax - limitMin) / 16);

  const handleChangeRankingScale = (e, id) => {
    const existingValues: AnswerChoicesRankingScaleType
      = answerUser[questionId]
        && (answerUser[questionId].answer_choices as AnswerChoicesRankingScaleType);
    const values = existingValues
      ? {
          ...existingValues,
          ranking_scale: {
            ...existingValues.ranking_scale,
            [id]: e,
          },
        }
      : {
          // initialize the object with undefined values
          choices: answer_choices.map(ac => ac['@id']),
          ranking_scale: answer_choices.reduce(
            (acc, cur) => ({ ...acc, [cur.id]: undefined }),
            {},
          ),
        };
    handleChangeAnswer(values, questionId, type);
  };

  const answer = answerUser[questionId];
  const choices = answer?.answer_choices as AnswerChoicesRankingScaleType;
  const rankingScale = choices?.ranking_scale;

  useLayoutEffect(() => {
    setHandleFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRef.current]);

  return (
    <section data-testid="ranking-scale">
      {answer_choices && answer_choices.length > 0 && (
        <>
          <div className="ranking-scale-choices-caption">
            {answer_choices.map((choice, index) => {
              return (
                <div key={`choice-${choice.id}`}>
                  <span className="item-letter">{itemsLetters[index]}</span>
                  <span lang={langValue} className="item-label">
                    {choice.title}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="ranking-scale-labels">
            <span lang={langValue}>{label_min}</span>
            <span lang={langValue}>{label_max}</span>
          </div>
          <div className="ranking-scale-container" data-testid="slider">
            {answer_choices.map((ac: ChoiceType, i: number) => {
              const handleIdBase = `question-${getIdFromUri(questionId)}`;
              const handleId
                = i === 0 ? handleIdBase : `${handleIdBase}-${i + 1}`;
              const shouldHaveRef = i === 0;

              return (
                <Slider
                  key={`slider-${ac.id}`}
                  className={`${type} ${
                    answerUser[questionId]
                    && (rankingScale[ac.id] || rankingScale[ac.id] === 0)
                      ? ''
                      : 'unanswered'
                  }`}
                  min={limitMin}
                  max={limitMax}
                  defaultValue={limitMin + i * spaceBetweenHandle}
                  value={choices && rankingScale[ac.id]}
                  step={0.1}
                  onChange={e => handleChangeRankingScale(e, ac.id)}
                  handleRender={node => (
                    <div
                      {...node.props}
                      style={node.props.style}
                      id={handleId}
                      ref={shouldHaveRef ? handleRef : null}
                    >
                      {itemsLetters[i]}
                    </div>
                  )}
                  disabled={disabled}
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export interface RankingScaleTypeQuestionPropsType {
  answerUser: AnswerUserType;
  answer_choices: ChoiceType[];
  questionId: string;
  label_min: string;
  label_max: string;
  limit_min: number;
  limit_max: number;
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  langValue: string;
  disabled?: boolean;
}
